import { useEffect, useMemo } from 'react';

import { type IdentifyParams, useCanny } from '~/providers/CannyProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useWorkspaceMembersContext } from '~/providers/WorkspaceMembersProvider';
import { useBillableUsersCount } from '~/swr-hooks/members/useBillableUsersCount';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { useSubscriptionPlan } from '~/swr-hooks/subscriptions/useSubscriptionPlan';
import { CENTS_PER_DOLLAR } from '~/utils/CheckoutUtils';
import { getMonthlyPlanPriceCents } from '~/utils/SubscriptionUtils';

export const useCannyUrl = () => {
  const { canny, getCannyUrl } = useCanny();
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: subscription } = useSubscriptionContext();
  const { data: subscriptionPlan } = useSubscriptionPlan();
  const { totalBillableSeats } = useBillableUsersCount();
  const { data: workspaceMembers } = useWorkspaceMembersContext();
  const { data: storage } = useStorage();

  const planMonthlyTotalPrice = getMonthlyPlanPriceCents({
    subscription,
    plan: subscriptionPlan?.plan,
    totalBillableSeats,
  });

  const cannyParams = useMemo<Omit<IdentifyParams, 'appID'> | undefined>(() => {
    if (currentWorkspace && currentWorkspaceMember?.accountId) {
      return {
        user: {
          avatarURL: currentWorkspaceMember.avatar ?? '',
          email: currentWorkspaceMember.email,
          id: currentWorkspaceMember.accountId,
          name: `${currentWorkspaceMember.firstName} ${currentWorkspaceMember.lastName}`,
          companies: [
            {
              id: currentWorkspace.id,
              name: currentWorkspace.name,
              monthlySpend: planMonthlyTotalPrice ? planMonthlyTotalPrice / CENTS_PER_DOLLAR : 0,
              customFields: {
                plan: subscription?.plan,
                'Total Workspace Members': workspaceMembers?.length,
                'Total Workspace Storage': storage?.maxStorage,
              },
            },
          ],
        },
      };
    }
  }, [
    currentWorkspace,
    currentWorkspaceMember,
    planMonthlyTotalPrice,
    storage?.maxStorage,
    subscription?.plan,
    workspaceMembers?.length,
  ]);

  const cannyUrl = useMemo(() => {
    return cannyParams ? getCannyUrl(cannyParams) : undefined;
  }, [cannyParams, getCannyUrl]);

  useEffect(() => {
    if (cannyParams) {
      canny?.identify(cannyParams);
    }
  }, [canny, cannyParams]);

  return {
    cannyUrl,
  };
};
